import React, { useEffect, useMemo } from 'react';
import { useAPIClientRequest } from 'c-hooks';
import ApiClient from 'c-data/apiClient';
import type { ElasticSearchFrameIdMapDataResponse } from 'c-sdk';
import type { PcaReportFilters } from 'c-components/HiddenReport/reportFilters';
import { esFieldFilters, pcaReportFieldFilters } from 'c-components/HiddenReport/reportFilters';
import { ElasticSearchHistogramInterval, EsPCAReportField, PCAReportMetric } from 'c-sdk';
import { Box, CircularProgress, Grid, Stack, Typography } from '@mui/material';
import uniledLogo from 'public/uniledLogo.png';
import poweredByUniledLogo from 'public/poweredByUniledLogo.png';

import TwoChartPage from 'c-components/HiddenReport/Components/TwoChartPage';
import TwoMapPage from 'c-components/HiddenReport/Components/TwoMapPage';
import { mapDataToSqlMapData } from 'c-main/Components/Campaign/CampaignReports/ElasticSearch/Transformers';

import ReportFiltersLine from 'c-components/HiddenReport/Components/ReportFiltersLine';
import OneMapPage from '../Components/OneMapPage';

type ReportHeaderProps = {
    brand: string;
    logoUrl: string;
    mb?: number;
    showBrandBar?: boolean;
};

export function formatDate(dateString: string, locale: string): string {
    const date = new Date(dateString);
    // eslint-disable-next-line no-restricted-globals
    if (isNaN(date.getTime())) throw new Error('Invalid date string');
    const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
    const day = date.getUTCDate().toString().padStart(2, '0');
    const year = date.getUTCFullYear();
    return locale === 'GB' ? `${day}/${month}/${year}` : `${month}/${day}/${year}`;
}
export function reformatDateForFilteredDates(dateStr: string, locale: string): string {
    const ddmmyyyyRegex = /^\d{2}-\d{2}-\d{4}$/;
    if (ddmmyyyyRegex.test(dateStr)) {
        const [dd, mm, yyyy] = dateStr.split('-');
        return locale === 'GB' ? `${dd}/${mm}/${yyyy}` : `${mm}/${dd}/${yyyy}`;
    }
    const [yyyy, mm, dd] = dateStr.split('-');
    if (!yyyy || !mm || !dd) throw new Error(`Unrecognized date format: ${dateStr}`);
    return locale === 'GB' ? `${dd}/${mm}/${yyyy}` : `${mm}/${dd}/${yyyy}`;
}
const nf = new Intl.NumberFormat('en-GB', { maximumFractionDigits: 0 });
const fmtInt = (n?: number) => (typeof n === 'number' ? nf.format(Math.round(n)) : '0');

const cumulative = (vals: number[]) => {
    let s = 0;
    // eslint-disable-next-line no-return-assign
    return vals.map(v => (s += v || 0));
};

const fmtCompact = (n?: number) => {
    // eslint-disable-next-line no-restricted-globals
    if (typeof n !== 'number' || !isFinite(n)) return '0';
    const sign = n < 0 ? '-' : '';
    const v = Math.abs(n);
    if (v >= 1e9) return `${sign}${+(v / 1e9).toFixed(v >= 1e11 ? 0 : 1)}b`;
    if (v >= 1e6) return `${sign}${+(v / 1e6).toFixed(v >= 1e7 ? 0 : 1)}m`;
    if (v >= 1e3) return `${sign}${+(v / 1e3).toFixed(v >= 1e4 ? 0 : 1)}k`;
    return `${sign}${nf.format(Math.round(v))}`;
};

const getBucket = (raw: any, iso: string) => raw?.[iso]?.[iso] ?? raw?.[iso] ?? {};
const DOW_ORDER = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const HIDDEN_REPORT_MAX_BAR_ITEMS = 5;

type SummaryBarRow = {
    name: string;
    impacts_total: number;
    impacts_expected: number;
    plays_total: number;
    plays_expected: number;
};

const sortAndGroupTopBarRows = (
    rows: SummaryBarRow[],
    maxItems = HIDDEN_REPORT_MAX_BAR_ITEMS,
): SummaryBarRow[] => {
    const sortedRows = [...rows].sort((a, b) => {
        const aScore = Math.max(
            a.impacts_total,
            a.plays_total,
            a.impacts_expected,
            a.plays_expected,
        );
        const bScore = Math.max(
            b.impacts_total,
            b.plays_total,
            b.impacts_expected,
            b.plays_expected,
        );
        return bScore - aScore;
    });

    if (sortedRows.length <= maxItems) return sortedRows;

    const topRows = sortedRows.slice(0, maxItems);
    const others = sortedRows.slice(maxItems).reduce<SummaryBarRow>(
        (acc, row) => ({
            name: 'Others',
            impacts_total: acc.impacts_total + row.impacts_total,
            impacts_expected: acc.impacts_expected + row.impacts_expected,
            plays_total: acc.plays_total + row.plays_total,
            plays_expected: acc.plays_expected + row.plays_expected,
        }),
        {
            name: 'Others',
            impacts_total: 0,
            impacts_expected: 0,
            plays_total: 0,
            plays_expected: 0,
        },
    );

    return [...topRows, others];
};

const ReportHeader: React.FC<ReportHeaderProps> = ({
    brand,
    logoUrl,
    mb = 3,
    showBrandBar = false,
}) => (
    <>
        {showBrandBar && <Box sx={{ height: 10, bgcolor: brand, borderRadius: 1, mb }} />}

        <Stack pl={3} pt={2} direction="row" alignItems="flex-end" gap={2} sx={{ width: 1, mb }}>
            <Box
                component="img"
                src={logoUrl}
                alt="Logo"
                className="header-logo"
                sx={{
                    maxHeight: 48,
                    maxWidth: 270,
                    objectFit: 'contain',
                    display: 'block',
                }}
            />
        </Stack>
    </>
);

const ReportFooter = ({
    title,
    campaignId,
    brand,
}: {
    title: string;
    campaignId: number | string;
    brand: string;
}) => (
    <Box>
        <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mt={-3}
            sx={{ px: 2, py: 0.5 }}
        >
            <Typography sx={{ fontWeight: 800 }} pl={5}>
                {title} | # {campaignId}
            </Typography>
            <Box
                pr={2}
                component="img"
                src={poweredByUniledLogo}
                alt="Powered by UniLED"
                sx={{ maxHeight: 48, maxWidth: 270, objectFit: 'contain', display: 'block' }}
            />
        </Box>
        <Box sx={{ height: 40, bgcolor: brand }} mt={1.5} />
    </Box>
);
interface Payload {
    campaign_id: number;
    jwt: string;
    powerpoint_template: ReportTemplate;
    skin_id: number;
    environment: Environment;
    email: string;
    language: 'GB' | 'US';
    start_date?: string;
    end_date?: string;
    third_color?: string;
    filters?: PcaReportFilters;
}
type Environment = 'local' | 'production' | 'staging';
type ReportTemplate = 'Linear_report' | 'DSP_report';

const LinearReport = ({ payload }: { payload: Payload }) => {
    const isTalon = useMemo(() => payload.skin_id === 7, [payload.skin_id]);
    const {
        start,
        data: campaign,
        isLoading: isCampaignLoading,
    } = useAPIClientRequest(ApiClient.Entities.Campaign.get);
    const {
        start: skinStart,
        data: skin,
        isLoading: isSkinLoading,
    } = useAPIClientRequest(ApiClient.Entities.Skin.get);
    const {
        start: pcaSummaryStart,
        data: pcaSummary,
        isLoading: isPcaSummaryLoading,
    } = useAPIClientRequest(ApiClient.Reports.pcaReport);
    const {
        start: histogramStart,
        data: histogram,
        isLoading: isHistogramLoading,
    } = useAPIClientRequest(ApiClient.ReportsElasticSearch.histogram);
    const {
        start: envStart,
        data: env,
        isLoading: isEnvLoading,
    } = useAPIClientRequest(ApiClient.ReportsElasticSearch.pcaGroupByField);
    const {
        start: regionStart,
        data: region,
        isLoading: isRegionLoading,
    } = useAPIClientRequest(ApiClient.ReportsElasticSearch.pcaGroupByField);
    const {
        start: formatStart,
        data: formatBreakdown,
        isLoading: isFormatLoading,
    } = useAPIClientRequest(ApiClient.ReportsElasticSearch.pcaGroupByField);
    const {
        start: envFormatStart,
        data: envFormatBreakdown,
        isLoading: isEnvFormatLoading,
    } = useAPIClientRequest(ApiClient.ReportsElasticSearch.pcaGroupByField);
    const {
        start: dowStart,
        data: dow,
        isLoading: isDowLoading,
    } = useAPIClientRequest(ApiClient.ReportsElasticSearch.pcaGroupByField);
    const {
        start: hourStart,
        data: hour,
        isLoading: isHourLoading,
    } = useAPIClientRequest(ApiClient.ReportsElasticSearch.pcaGroupByField);

    const {
        start: fetchMapData,
        data: mapResp,
        isLoading: isMapLoading,
    } = useAPIClientRequest(ApiClient.ReportsElasticSearch.frameIdMapData);

    const n = (v?: number) => (typeof v === 'number' ? Math.round(v) : 0);
    const fmtN = (v?: number) => n(v).toLocaleString();
    const pct = (actual?: number, expected?: number) =>
        expected && expected > 0 ? (actual / expected) * 100 : null;
    const fmtPct = (p: number | null) => (p == null ? '-' : `${p.toFixed(1)}%`);
    const perfColor = (p: number | null) =>
        p == null ? 'text.primary' : p >= 100 ? '#158f43' : '#e11d48';

    useEffect(() => {
        if (!payload?.campaign_id || !payload?.skin_id) return;

        start(payload.campaign_id, [
            'agency',
            'market',
            'market.country',
            'creatives',
            'client',
            'creatives.resolution',
            'creatives.file',
            'date',
            'budgetSummary',
            'budgetSummary.lineItems',
            'budgetSummaries',
            'budgetSummaries.lineItems',
            'notices',
            'errors',
            'creativeThumbnails',
            'bookingSummaries',
            'popShotsCount',
            'missingDisplaysCount',
            'additionalContacts',
        ]);

        skinStart(payload.skin_id);
        histogramStart({
            campaignId: payload.campaign_id,
            field: EsPCAReportField.StartDate,
            interval: ElasticSearchHistogramInterval.Day,
            startDate: payload?.start_date ?? null,
            endDate: payload?.end_date ?? null,
            fieldFilters: esFieldFilters(payload.filters),
        });
        fetchMapData({
            campaignId: payload.campaign_id,
            startDate: payload?.start_date ?? null,
            endDate: payload?.end_date ?? null,
            fieldFilters: esFieldFilters(payload.filters),
        });
        pcaSummaryStart({
            campaignId: payload.campaign_id,
            startDate: payload?.start_date ?? null,
            endDate: payload?.end_date ?? null,
            timeFrame: 'summary' as any,
            fieldFilters: pcaReportFieldFilters(payload.filters),
        });
        envStart({
            campaignId: payload.campaign_id,
            startDate: payload?.start_date ?? null,
            endDate: payload?.end_date ?? null,
            fieldFilters: esFieldFilters(payload.filters),
            field: EsPCAReportField.DisplayEnv,
        });
        regionStart({
            campaignId: payload.campaign_id,
            startDate: payload?.start_date ?? null,
            endDate: payload?.end_date ?? null,
            fieldFilters: esFieldFilters(payload.filters),
            field: EsPCAReportField.DisplayTvRegion,
        });
        formatStart({
            campaignId: payload.campaign_id,
            startDate: payload?.start_date ?? null,
            endDate: payload?.end_date ?? null,
            fieldFilters: esFieldFilters(payload.filters),
            field: EsPCAReportField.DisplayFormat,
        });
        if (isTalon) {
            envFormatStart({
                campaignId: payload.campaign_id,
                startDate: payload?.start_date ?? null,
                endDate: payload?.end_date ?? null,
                fieldFilters: esFieldFilters(payload.filters),
                fields: [EsPCAReportField.DisplayEnv, EsPCAReportField.DisplayFormat],
            });
        }
        dowStart({
            campaignId: payload.campaign_id,
            startDate: payload?.start_date ?? null,
            endDate: payload?.end_date ?? null,
            fieldFilters: esFieldFilters(payload.filters),
            field: EsPCAReportField.Day,
        });
        hourStart({
            campaignId: payload.campaign_id,
            startDate: payload?.start_date ?? null,
            endDate: payload?.end_date ?? null,
            fieldFilters: esFieldFilters(payload.filters),
            field: EsPCAReportField.Hour,
        });
    }, [
        payload,
        start,
        skinStart,
        pcaSummaryStart,
        histogramStart,
        envStart,
        regionStart,
        formatStart,
        envFormatStart,
        dowStart,
        hourStart,
        fetchMapData,
        isTalon,
    ]);

    const isReady = useMemo(
        () =>
            !!campaign &&
            !!skin &&
            !!pcaSummary &&
            !!histogram &&
            !!env &&
            !!region &&
            !!formatBreakdown &&
            (!isTalon || !!envFormatBreakdown) &&
            !!dow &&
            !!hour &&
            !!mapResp,
        [
            campaign,
            dow,
            env,
            envFormatBreakdown,
            formatBreakdown,
            histogram,
            hour,
            isTalon,
            mapResp,
            pcaSummary,
            region,
            skin,
        ],
    );

    useEffect(() => {
        if (isReady) {
            // eslint-disable-next-line no-underscore-dangle
            window.__REACT_READY__ = true;
        }
    }, [isReady]);

    const mapData = useMemo(() => {
        const raw = mapResp as any;
        const nestedData = raw?.data?.data;
        const directData = raw?.data;
        const reportData = raw?.data?.reportData ?? raw?.reportData;

        if (reportData && typeof reportData === 'object' && !Array.isArray(reportData)) {
            return reportData;
        }

        let candidate = null;
        if (nestedData && typeof nestedData === 'object' && !Array.isArray(nestedData)) {
            candidate = nestedData;
        } else if (directData && typeof directData === 'object' && !Array.isArray(directData)) {
            candidate = directData;
        }

        if (!candidate) return {};

        return mapDataToSqlMapData(PCAReportMetric.Impacts, {
            data: candidate,
            filters: raw?.data?.filters ?? raw?.filters ?? {},
            campaign_stats: raw?.data?.campaign_stats ?? raw?.campaign_stats,
        } as ElasticSearchFrameIdMapDataResponse).reportData;
    }, [mapResp]);

    const brand = useMemo(
        () =>
            (skin as any)?.data?.data?.primary_color ||
            (skin as any)?.data?.primary_color ||
            (skin as any)?.data?.data?.third_color ||
            (skin as any)?.data?.third_color ||
            'rgba(31,41,55,1)',
        [skin],
    );

    const logoUrl = useMemo(() => (skin as any)?.data?.data?.logo_path ?? '', [skin]);

    const c = (campaign as any)?.data?.data;
    const title: string = c?.name || 'Campaign Report';
    const reference: string = c?.reference || '';
    const referenceText = reference ? ` (${reference})` : '';

    const liveDates =
        c?.date_start && c?.date_end
            ? `${formatDate(c.date_start, payload.language)} – ${formatDate(
                  c.date_end,
                  payload.language,
              )}`
            : '';

    const reportDates =
        payload.start_date && payload.end_date
            ? `${reformatDateForFilteredDates(
                  payload.start_date,
                  payload.language,
              )} – ${reformatDateForFilteredDates(payload.end_date, payload.language)}`
            : (() => {
                  const daily = (pcaSummary as any)?.data?.data?.daily_stats;
                  if (daily && typeof daily === 'object') {
                      const keys = Object.keys(daily).sort();
                      if (keys.length) {
                          return `${reformatDateForFilteredDates(
                              keys[0],
                              payload.language,
                          )} – ${reformatDateForFilteredDates(
                              keys[keys.length - 1],
                              payload.language,
                          )}`;
                      }
                  }
                  return '';
              })();

    const linkHref = `https://app.uniledsoftware.com/campaigns/${payload.campaign_id}`;

    const impactsActual = (pcaSummary as any)?.data?.data?.campaign_stats?.impacts;
    const impactsExpected = (pcaSummary as any)?.data?.data?.campaign_stats
        ?.expected_impacts_in_schedule;
    const playsActual = (pcaSummary as any)?.data?.data?.campaign_stats?.sum_of_plays;
    const playsExpected = (pcaSummary as any)?.data?.data?.campaign_stats
        ?.expected_plays_in_schedule;
    const timeActual = (pcaSummary as any)?.data?.data?.campaign_stats?.sum_of_time; // seconds
    const timeExpected = (pcaSummary as any)?.data?.data?.campaign_stats?.expected_time_in_schedule; // seconds

    const impactsP = pct(impactsActual, impactsExpected);
    const playsP = pct(playsActual, playsExpected);
    const timeP = pct(timeActual, timeExpected);

    const fmtTime = (sec?: number) => {
        if (sec == null) return '0m';
        const totalM = Math.round(sec / 60);
        const h = Math.floor(totalM / 60);
        const m = totalM % 60;
        return h > 0 ? `${h.toLocaleString()} hrs ${m}m` : `${m}m`;
    };
    const rawHist = useMemo(
        () => (histogram as any)?.data?.data ?? (histogram as any)?.data ?? {},
        [histogram],
    );
    const dates = useMemo(() => Object.keys(rawHist).sort(), [rawHist]);

    const daily = useMemo(
        () =>
            dates.map(iso => {
                const b = getBucket(rawHist, iso);
                return {
                    iso,
                    label: formatDate(iso, payload.language),
                    impacts_actual: b?.impacts ?? 0,
                    impacts_expected: b?.expected_impacts_in_schedule ?? 0,
                    plays_actual: b?.sum_of_plays ?? 0,
                    plays_expected: b?.expected_plays_in_schedule ?? 0,
                };
            }),
        [dates, rawHist, payload.language],
    );

    const rtImpacts = useMemo(() => {
        const a = cumulative(daily.map(d => d.impacts_actual));
        const e = cumulative(daily.map(d => d.impacts_expected));
        return daily.map((d, i) => ({ label: d.label, Total: a[i], Expected: e[i] }));
    }, [daily]);

    const rtPlays = useMemo(() => {
        const a = cumulative(daily.map(d => d.plays_actual));
        const e = cumulative(daily.map(d => d.plays_expected));
        return daily.map((d, i) => ({ label: d.label, Total: a[i], Expected: e[i] }));
    }, [daily]);

    const histImpactsPct = impactsExpected > 0 ? (impactsActual / impactsExpected) * 100 : null;
    const histPlaysPct = playsExpected > 0 ? (playsActual / playsExpected) * 100 : null;

    const ownersRaw = useMemo(
        () =>
            (pcaSummary as any)?.data?.data?.display_owner ??
            (pcaSummary as any)?.data?.display_owner ??
            {},
        [pcaSummary],
    );

    type OwnerRow = SummaryBarRow;

    const owners: OwnerRow[] = useMemo(
        () =>
            sortAndGroupTopBarRows(
                Object.entries(ownersRaw).map(([name, v]: [string, any]) => ({
                    name,
                    impacts_total: v?.impacts ?? 0,
                    impacts_expected: v?.expected_impacts_in_schedule ?? 0,
                    plays_total: v?.sum_of_plays ?? 0,
                    plays_expected: v?.expected_plays_in_schedule ?? 0,
                })),
            ),
        [ownersRaw],
    );
    const envRaw = useMemo(() => (env as any)?.data?.data ?? (env as any)?.data ?? {}, [env]);

    const hasAnyMetrics = (v: {
        impacts_total: number;
        impacts_expected: number;
        plays_total: number;
        plays_expected: number;
    }) =>
        v?.impacts_total !== 0 ||
        v?.impacts_expected !== 0 ||
        v?.plays_total !== 0 ||
        v?.plays_expected !== 0;

    const envRows: SummaryBarRow[] = useMemo(
        () =>
            sortAndGroupTopBarRows(
                Object.entries(envRaw)
                    .map(([name, v]: [string, any]) => ({
                        name,
                        impacts_total: v?.impacts ?? 0,
                        impacts_expected: v?.expected_impacts_in_schedule ?? 0,
                        plays_total: v?.sum_of_plays ?? 0,
                        plays_expected: v?.expected_plays_in_schedule ?? 0,
                    }))
                    .filter(hasAnyMetrics),
            ),
        [envRaw],
    );
    const regionRaw = useMemo(
        () => (region as any)?.data?.data ?? (region as any)?.data ?? {},
        [region],
    );

    const regionRows: SummaryBarRow[] = useMemo(
        () =>
            sortAndGroupTopBarRows(
                Object.entries(regionRaw)
                    .map(([name, v]: [string, any]) => ({
                        name,
                        impacts_total: v?.impacts ?? 0,
                        impacts_expected: v?.expected_impacts_in_schedule ?? 0,
                        plays_total: v?.sum_of_plays ?? 0,
                        plays_expected: v?.expected_plays_in_schedule ?? 0,
                    }))
                    .filter(hasAnyMetrics),
            ),
        [regionRaw],
    );
    const formatRaw = useMemo(
        () => (formatBreakdown as any)?.data?.data ?? (formatBreakdown as any)?.data ?? {},
        [formatBreakdown],
    );

    const formatRows: SummaryBarRow[] = useMemo(
        () =>
            sortAndGroupTopBarRows(
                Object.entries(formatRaw)
                    .map(([name, v]: [string, any]) => ({
                        name,
                        impacts_total: v?.impacts ?? 0,
                        impacts_expected: v?.expected_impacts_in_schedule ?? 0,
                        plays_total: v?.sum_of_plays ?? 0,
                        plays_expected: v?.expected_plays_in_schedule ?? 0,
                    }))
                    .filter(hasAnyMetrics),
            ),
        [formatRaw],
    );
    const envFormatRaw = useMemo(
        () => (envFormatBreakdown as any)?.data?.data ?? (envFormatBreakdown as any)?.data ?? {},
        [envFormatBreakdown],
    );

    const envFormatRows: SummaryBarRow[] = useMemo(
        () =>
            sortAndGroupTopBarRows(
                Object.entries(envFormatRaw).flatMap(([envName, nested]: [string, any]) => {
                    if (!nested || typeof nested !== 'object') return [];

                    return Object.entries(nested)
                        .map(([formatName, v]: [string, any]) => ({
                            name: `${envName} - ${formatName}`,
                            impacts_total: v?.impacts ?? 0,
                            impacts_expected: v?.expected_impacts_in_schedule ?? 0,
                            plays_total: v?.sum_of_plays ?? 0,
                            plays_expected: v?.expected_plays_in_schedule ?? 0,
                        }))
                        .filter(hasAnyMetrics);
                }),
            ),
        [envFormatRaw],
    );
    // Map 0..6 -> weekday names where 0 = Monday.
    const dowName = (i: number, language: string) =>
        new Date(Date.UTC(2023, 0, 2 + i)).toLocaleDateString(
            language === 'GB' ? 'en-GB' : 'en-US',
            { weekday: 'long' },
        );

    const dowRaw = useMemo(() => (dow as any)?.data?.data ?? (dow as any)?.data ?? {}, [dow]);

    const dowRows: {
        name: string;
        impacts_total: number;
        impacts_expected: number;
        plays_total: number;
        plays_expected: number;
    }[] = useMemo(() => {
        const normalizeDowName = (key: string) => {
            if (/^\d+$/.test(key)) {
                const rawIndex = parseInt(key, 10);
                const zeroBasedIndex =
                    rawIndex >= 0 && rawIndex <= 6
                        ? rawIndex
                        : rawIndex >= 1 && rawIndex <= 7
                        ? rawIndex - 1
                        : null;

                if (zeroBasedIndex != null) {
                    return dowName(zeroBasedIndex, payload.language);
                }
            }

            const normalizedKey = key.trim().toLowerCase();
            const matched = DOW_ORDER.find(day => day.toLowerCase() === normalizedKey);
            if (matched) return matched;

            const matchedShort = DOW_ORDER.find(day =>
                day.toLowerCase().startsWith(normalizedKey.slice(0, 3)),
            );
            return matchedShort ?? key;
        };

        const sourceEntries = Object.keys(dowRaw ?? {}).length
            ? Object.entries(dowRaw as Record<string, any>)
            : ['0', '1', '2', '3', '4', '5', '6'].map(k => [k, (dowRaw as any)?.[k] ?? {}]);

        const combined = sourceEntries.reduce<Record<string, any>>((acc, [rawKey, value]) => {
            const name = normalizeDowName(String(rawKey));
            const current = acc[name] ?? {
                name,
                impacts_total: 0,
                impacts_expected: 0,
                plays_total: 0,
                plays_expected: 0,
            };

            acc[name] = {
                name,
                impacts_total: current.impacts_total + (value?.impacts ?? 0),
                impacts_expected:
                    current.impacts_expected + (value?.expected_impacts_in_schedule ?? 0),
                plays_total: current.plays_total + (value?.sum_of_plays ?? 0),
                plays_expected: current.plays_expected + (value?.expected_plays_in_schedule ?? 0),
            };

            return acc;
        }, {});

        return DOW_ORDER.map(name => combined[name]).filter(hasAnyMetrics);
    }, [dowRaw, payload.language]);

    const hourRaw = useMemo(() => (hour as any)?.data?.data ?? (hour as any)?.data ?? {}, [hour]);

    const hourRows: {
        name: string;
        impacts_total: number;
        impacts_expected: number;
        plays_total: number;
        plays_expected: number;
    }[] = useMemo(() => {
        const keys = Array.from({ length: 24 }, (_, i) => String(i));
        const label = (k: string) => `${k.padStart(2, '0')}`;
        const rows = keys.map(k => {
            const v = (hourRaw as any)?.[k] ?? {};
            return {
                name: label(k),
                impacts_total: v?.impacts ?? 0,
                impacts_expected: v?.expected_impacts_in_schedule ?? 0,
                plays_total: v?.sum_of_plays ?? 0,
                plays_expected: v?.expected_plays_in_schedule ?? 0,
            };
        });

        return isTalon ? rows : rows.filter(hasAnyMetrics);
    }, [hourRaw, isTalon]);

    const third_color = payload?.third_color
        ? skin?.data?.data?.third_color
        : skin?.data?.data?.primary_color;

    if (!isReady) {
        return (
            <Box sx={{ minHeight: 320, display: 'grid', placeItems: 'center' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <>
            <style>{`
  /* A4 landscape, zero browser margins */
  @page {
    size: A4 landscape;
    margin: 0;
  }

  :root {
    --page-w: 297mm;
    --page-h: 210mm;
    --header-h: 84px;
    --footer-h: 64px;
    --pad-x: 24px;
    --pad-top-extra: 16px;
    --pad-bot-extra: 16px;
  }

  @media screen {
    .page {
      width: 1200px;
      min-height: 700px;
      margin: 16px auto;
      background: #fff;
      position: relative;
      box-sizing: border-box;
      padding: calc(var(--header-h) + var(--pad-top-extra)) var(--pad-x) calc(var(--footer-h) + var(--pad-bot-extra));
    }
    .page + .page { margin-top: 24px; }
    .page .page-header { position: absolute; top: 0; left: 0; right: 0; height: var(--header-h); }
    .page .page-footer { position: absolute; bottom: 0; left: 0; right: 0; height: var(--footer-h); }
    .page .center-divider {
      position: absolute;
      left: 50%;
      transform: translateX(-0.5px);
      top: calc(var(--header-h) + 56px);
      bottom: calc(var(--footer-h) + 100px);
      border-left: 1px solid #d9d9d9;
      pointer-events: none;
      z-index: 2;
    }
  }

  @media print {
    html, body { margin: 0 !important; padding: 0 !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .page {
      width: var(--page-w) !important;
      min-height: var(--page-h) !important;
      height: var(--page-h) !important;
      page-break-after: always;
      background: #fff;
      position: relative;
      box-sizing: border-box;
      padding: calc(var(--header-h) + var(--pad-top-extra)) var(--pad-x) calc(var(--footer-h) + var(--pad-bot-extra));
      overflow: hidden;
    }
  
  .glossary-grid{
    grid-template-columns: max-content 1.25ch 1fr !important; /* keeps "=" close to the term */
    row-gap: 30px !important; /* was larger; tighten for print */
  }
  .final-title{
    font-size: 95px !important;  /* slightly bigger in print */
    line-height: 0.9 !important;
  }
  .final-logo{
    height: 48px !important;      /* logos a bit bigger on print */
  }
  .final-bar{
    height: 12px !important;      /* slightly thicker accent bar */
    width: 470px !important;      /* make it a touch wider */
  }
.header-logo{
  max-height: 30px !important;   /* was 48px on screen */
  max-width: 180px !important;   /* keep aspect ratio, just caps the width */
}

    .page:last-child { page-break-after: auto; }
    .page .page-header { position: absolute; top: 0; left: 0; right: 0; height: var(--header-h); background: #fff; }
    .page .page-footer { position: absolute; bottom: 0; left: 0; right: 0; height: var(--footer-h); background: #fff; }

    /* grey center divider visible across the smediaafe area */
    .page .center-divider {
      position: absolute;
      left: 50%;
      transform: translateX(-0.5px);
      top: calc(var(--header-h) + 50px);
    bottom: calc(var(--footer-h) + 100px);
      border-left: 1px solid #d9d9d9;
      pointer-events: none;
      z-index: 2;
    }
     .summary-row { 
    display: flex !important; 
    align-items: flex-start !important; 
  }
  .summary-row > .summary-left  { 
    width: 65% !important; 
    flex-basis: 65% !important; 
  }
  .summary-row > .summary-right { 
    width: 35% !important; 
    flex-basis: 35% !important; 
  }
  /* keep the button tight to the top in print */
  .summary-cta { margin-top: 0 !important; }
    .triplet > .MuiGrid-item {
  width: 33.3333% !important;
  flex-basis: 33.3333% !important;
}

    /* Force columns so content doesn't stack */
    .three-col > .MuiGrid-item { width: 33.3333% !important; flex-basis: 33.3333% !important; }
    .two-col   > .MuiGrid-item { width: 50% !important;      flex-basis: 50% !important; }

    /* Make page-1 metric cards shorter */
    .metrics .metric-card { padding: 10px !important; }
    .metrics .metric-title { font-size: 18px !important; line-height: 1.15 !important; margin-bottom: 2px !important; }
    .metrics .metric-perf-label { font-size: 12px !important; }
    .metrics .metric-perf-value { font-size: 18px !important; line-height: 1.1 !important; }
    .metrics .metric-row-label,
    .metrics .metric-row-value { font-size: 12px !important; }
  }
      `}</style>
            {/* --------- PAGE 1 --------- */}
            <Box className="page" sx={{ mx: 'auto', fontFamily: 'Inter, system-ui, sans-serif' }}>
                <Box className="page-header">
                    <ReportHeader brand={brand} logoUrl={logoUrl} />
                </Box>

                <Box sx={{ bgcolor: '#f3f4f6', borderRadius: 2, px: 2, py: 1.25, mb: 2 }} mt={5}>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: brand, pb: 1.25 }}>
                        Campaign Summary
                    </Typography>

                    <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2, mb: 1.5 }}>
                        {title}
                        <Typography component="span" variant="h6" sx={{ fontWeight: 500 }}>
                            {referenceText}
                        </Typography>
                    </Typography>

                    <Grid container spacing={2} alignItems="flex-start" className="summary-row">
                        <Grid item xs={12} md={7} className="summary-left">
                            <Stack spacing={0.75}>
                                {liveDates && (
                                    <Box>
                                        <Typography variant="caption" sx={{ color: 'grey.600' }}>
                                            Campaign Live Dates
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            {liveDates}
                                        </Typography>
                                    </Box>
                                )}

                                <Box>
                                    <Typography variant="caption" sx={{ color: 'grey.600' }}>
                                        Current Report Dates
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        {reportDates}
                                    </Typography>
                                </Box>

                                <ReportFiltersLine
                                    filters={payload.filters}
                                    language={payload.language}
                                    variant="stack"
                                />

                                <Box>
                                    <Typography variant="caption" sx={{ color: 'grey.600' }}>
                                        Campaign Number
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        #{payload.campaign_id}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Grid>

                        {!isTalon && (
                            <Grid item xs={12} md={5} className="summary-right">
                                <Box
                                    className="summary-cta"
                                    sx={{ backgroundColor: brand, borderRadius: 2, p: 1.5 }}
                                >
                                    <Typography
                                        component="a"
                                        href={linkHref}
                                        target="_blank"
                                        rel="noreferrer"
                                        display="flex"
                                        justifyContent="center"
                                        sx={{
                                            color: 'white',
                                            textAlign: 'center',
                                            fontWeight: 600,
                                            fontSize: 14,
                                        }}
                                    >
                                        Click here to view the full campaign performance on UniLED
                                    </Typography>
                                </Box>
                            </Grid>
                        )}
                    </Grid>
                </Box>

                <Box sx={{ mt: 8 }}>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: brand, mb: 1.5 }}>
                        Performance Headlines
                    </Typography>

                    {/* three smaller cards on print */}
                    <Grid container spacing={1.5} className="three-col metrics">
                        <Grid item xs={12} md={4}>
                            <Box
                                className="metric-card"
                                sx={{
                                    p: 1.5,
                                    borderRadius: 2,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                }}
                            >
                                <Typography
                                    className="metric-title"
                                    variant="h6"
                                    sx={{ color: brand }}
                                    display="flex"
                                    fontWeight={700}
                                    justifyContent="center"
                                >
                                    {payload.language === 'GB' ? 'Impacts' : 'Impressions'}
                                </Typography>
                                <Box display="flex" justifyContent="space-between">
                                    <Typography
                                        className="metric-perf-label"
                                        sx={{ color: 'text.secondary', mt: 0.25 }}
                                    >
                                        Performance
                                    </Typography>
                                    <Typography
                                        className="metric-perf-value"
                                        sx={{
                                            fontWeight: 800,
                                            color: perfColor(impactsP),
                                            lineHeight: 1.1,
                                        }}
                                    >
                                        {fmtPct(impactsP)}
                                    </Typography>
                                </Box>
                                <Box display="flex" justifyContent="space-between">
                                    <Typography
                                        className="metric-row-label"
                                        sx={{ color: 'text.secondary', mt: 0.25 }}
                                    >
                                        Total
                                    </Typography>
                                    <Typography
                                        className="metric-row-value"
                                        sx={{ fontWeight: 600 }}
                                    >
                                        {fmtN(impactsActual)}
                                    </Typography>
                                </Box>
                                <Box display="flex" justifyContent="space-between">
                                    <Typography
                                        className="metric-row-label"
                                        sx={{ color: 'text.secondary', mt: 0.25 }}
                                    >
                                        Expected
                                    </Typography>
                                    <Typography
                                        className="metric-row-value"
                                        sx={{ fontWeight: 600 }}
                                    >
                                        {fmtN(impactsExpected)}
                                    </Typography>
                                </Box>
                                <Box display="flex" justifyContent="space-between">
                                    <Typography
                                        className="metric-row-label"
                                        sx={{ color: 'text.secondary', mt: 0.25 }}
                                    >
                                        Variance
                                    </Typography>
                                    <Typography
                                        className="metric-row-value"
                                        sx={{
                                            fontWeight: 600,
                                            color:
                                                impactsActual - impactsExpected >= 0
                                                    ? '#158f43'
                                                    : '#e11d48',
                                        }}
                                    >
                                        {impactsActual - impactsExpected > 0 ? '+' : ''}
                                        {fmtN(impactsActual - impactsExpected)}
                                    </Typography>
                                </Box>
                                {!isTalon && (
                                    <Box display="flex" justifyContent="space-between">
                                        <Typography
                                            className="metric-row-label"
                                            sx={{ color: 'text.secondary', mt: 0.25 }}
                                        >
                                            In Schedule
                                        </Typography>
                                        <Typography className="metric-row-value" sx={{ mt: 0.25 }}>
                                            {fmtN(
                                                (pcaSummary as any)?.data?.data?.campaign_stats
                                                    ?.impacts_received_in_schedule,
                                            )}
                                        </Typography>
                                    </Box>
                                )}
                                {!isTalon && (
                                    <Box display="flex" justifyContent="space-between">
                                        <Typography
                                            className="metric-row-label"
                                            sx={{ color: 'text.secondary', mt: 0.25 }}
                                        >
                                            Out Schedule
                                        </Typography>
                                        <Typography className="metric-row-value" sx={{ mt: 0.25 }}>
                                            {fmtN(
                                                (pcaSummary as any)?.data?.data?.campaign_stats
                                                    ?.impacts_received_out_of_schedule,
                                            )}
                                        </Typography>
                                    </Box>
                                )}
                                {!isTalon && (
                                    <Box display="flex" justifyContent="space-between">
                                        <Typography
                                            className="metric-row-label"
                                            sx={{ color: 'text.secondary', mt: 0.25 }}
                                        >
                                            Unbooked
                                        </Typography>
                                        <Typography className="metric-row-value" sx={{ mt: 0.25 }}>
                                            {fmtN(
                                                (pcaSummary as any)?.data?.data?.campaign_stats
                                                    ?.impacts_received_unbooked,
                                            )}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <Box
                                className="metric-card"
                                sx={{
                                    p: 1.5,
                                    borderRadius: 2,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                }}
                            >
                                <Typography
                                    className="metric-title"
                                    variant="h6"
                                    sx={{ color: brand }}
                                    display="flex"
                                    fontWeight={700}
                                    justifyContent="center"
                                >
                                    Plays
                                </Typography>
                                <Box display="flex" justifyContent="space-between">
                                    <Typography
                                        className="metric-perf-label"
                                        sx={{ color: 'text.secondary', mt: 0.25 }}
                                    >
                                        Performance
                                    </Typography>
                                    <Typography
                                        className="metric-perf-value"
                                        sx={{
                                            fontWeight: 800,
                                            color: perfColor(playsP),
                                            lineHeight: 1.1,
                                        }}
                                    >
                                        {fmtPct(playsP)}
                                    </Typography>
                                </Box>
                                <Box display="flex" justifyContent="space-between">
                                    <Typography
                                        className="metric-row-label"
                                        sx={{ color: 'text.secondary', mt: 0.25 }}
                                    >
                                        Total
                                    </Typography>
                                    <Typography
                                        className="metric-row-value"
                                        sx={{ fontWeight: 600 }}
                                    >
                                        {fmtN(playsActual)}
                                    </Typography>
                                </Box>
                                <Box display="flex" justifyContent="space-between">
                                    <Typography
                                        className="metric-row-label"
                                        sx={{ color: 'text.secondary', mt: 0.25 }}
                                    >
                                        Expected
                                    </Typography>
                                    <Typography
                                        className="metric-row-value"
                                        sx={{ fontWeight: 600 }}
                                    >
                                        {fmtN(playsExpected)}
                                    </Typography>
                                </Box>
                                <Box display="flex" justifyContent="space-between">
                                    <Typography
                                        className="metric-row-label"
                                        sx={{ color: 'text.secondary', mt: 0.25 }}
                                    >
                                        Variance
                                    </Typography>
                                    <Typography
                                        className="metric-row-value"
                                        sx={{
                                            fontWeight: 600,
                                            color:
                                                playsActual - playsExpected >= 0
                                                    ? '#158f43'
                                                    : '#e11d48',
                                        }}
                                    >
                                        {playsActual - playsExpected === 0
                                            ? ''
                                            : playsActual - playsExpected > 0
                                            ? '+'
                                            : '-'}
                                        {fmtN(Math.abs(playsActual - playsExpected))}
                                    </Typography>
                                </Box>
                                {!isTalon && (
                                    <Box display="flex" justifyContent="space-between">
                                        <Typography
                                            className="metric-row-label"
                                            sx={{ color: 'text.secondary', mt: 0.25 }}
                                        >
                                            In Schedule
                                        </Typography>
                                        <Typography className="metric-row-value" sx={{ mt: 0.25 }}>
                                            {fmtN(
                                                (pcaSummary as any)?.data?.data?.campaign_stats
                                                    ?.plays_received_in_schedule,
                                            )}
                                        </Typography>
                                    </Box>
                                )}
                                {!isTalon && (
                                    <Box display="flex" justifyContent="space-between">
                                        <Typography
                                            className="metric-row-label"
                                            sx={{ color: 'text.secondary', mt: 0.25 }}
                                        >
                                            Out Schedule
                                        </Typography>
                                        <Typography className="metric-row-value" sx={{ mt: 0.25 }}>
                                            {fmtN(
                                                (pcaSummary as any)?.data?.data?.campaign_stats
                                                    ?.plays_received_out_of_schedule,
                                            )}
                                        </Typography>
                                    </Box>
                                )}
                                {!isTalon && (
                                    <Box display="flex" justifyContent="space-between">
                                        <Typography
                                            className="metric-row-label"
                                            sx={{ color: 'text.secondary', mt: 0.25 }}
                                        >
                                            Unbooked
                                        </Typography>
                                        <Typography className="metric-row-value" sx={{ mt: 0.25 }}>
                                            {fmtN(
                                                (pcaSummary as any)?.data?.data?.campaign_stats
                                                    ?.plays_received_unbooked,
                                            )}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <Box
                                className="metric-card"
                                sx={{
                                    p: 1.5,
                                    borderRadius: 2,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                }}
                            >
                                <Typography
                                    className="metric-title"
                                    variant="h6"
                                    sx={{ color: brand }}
                                    display="flex"
                                    fontWeight={700}
                                    justifyContent="center"
                                >
                                    Time Played
                                </Typography>
                                <Box display="flex" justifyContent="space-between">
                                    <Typography
                                        className="metric-perf-label"
                                        sx={{ color: 'text.secondary', mt: 0.25 }}
                                    >
                                        Performance
                                    </Typography>
                                    <Typography
                                        className="metric-perf-value"
                                        sx={{
                                            fontWeight: 800,
                                            color: perfColor(timeP),
                                            lineHeight: 1.1,
                                        }}
                                    >
                                        {fmtPct(timeP)}
                                    </Typography>
                                </Box>
                                <Box display="flex" justifyContent="space-between">
                                    <Typography
                                        className="metric-row-label"
                                        sx={{ color: 'text.secondary', mt: 0.25 }}
                                    >
                                        Total
                                    </Typography>
                                    <Typography
                                        className="metric-row-value"
                                        sx={{ fontWeight: 600 }}
                                    >
                                        {fmtTime(
                                            (pcaSummary as any)?.data?.data?.campaign_stats
                                                ?.sum_of_time,
                                        )}
                                    </Typography>
                                </Box>
                                <Box display="flex" justifyContent="space-between">
                                    <Typography
                                        className="metric-row-label"
                                        sx={{ color: 'text.secondary', mt: 0.25 }}
                                    >
                                        Expected
                                    </Typography>
                                    <Typography
                                        className="metric-row-value"
                                        sx={{ fontWeight: 600 }}
                                    >
                                        {fmtTime(
                                            (pcaSummary as any)?.data?.data?.campaign_stats
                                                ?.expected_time_in_schedule,
                                        )}
                                    </Typography>
                                </Box>
                                <Box display="flex" justifyContent="space-between">
                                    <Typography
                                        className="metric-row-label"
                                        sx={{ color: 'text.secondary', mt: 0.25 }}
                                    >
                                        Variance
                                    </Typography>
                                    <Typography
                                        className="metric-row-value"
                                        sx={{
                                            fontWeight: 600,
                                            color:
                                                (pcaSummary as any)?.data?.data?.campaign_stats
                                                    ?.sum_of_time -
                                                    (pcaSummary as any)?.data?.data?.campaign_stats
                                                        ?.expected_time_in_schedule >=
                                                0
                                                    ? '#158f43'
                                                    : '#e11d48',
                                        }}
                                    >
                                        {(pcaSummary as any)?.data?.data?.campaign_stats
                                            ?.sum_of_time -
                                            (pcaSummary as any)?.data?.data?.campaign_stats
                                                ?.expected_time_in_schedule ===
                                        0
                                            ? ''
                                            : (pcaSummary as any)?.data?.data?.campaign_stats
                                                  ?.sum_of_time -
                                                  (pcaSummary as any)?.data?.data?.campaign_stats
                                                      ?.expected_time_in_schedule >
                                              0
                                            ? '+'
                                            : '-'}
                                        {fmtTime(
                                            Math.abs(
                                                (pcaSummary as any)?.data?.data?.campaign_stats
                                                    ?.sum_of_time -
                                                    (pcaSummary as any)?.data?.data?.campaign_stats
                                                        ?.expected_time_in_schedule,
                                            ),
                                        )}
                                    </Typography>
                                </Box>
                                {!isTalon && (
                                    <Box display="flex" justifyContent="space-between">
                                        <Typography
                                            className="metric-row-label"
                                            sx={{ color: 'text.secondary', mt: 0.25 }}
                                        >
                                            In Schedule
                                        </Typography>
                                        <Typography className="metric-row-value" sx={{ mt: 0.25 }}>
                                            {fmtTime(
                                                (pcaSummary as any)?.data?.data?.campaign_stats
                                                    ?.time_received_in_schedule,
                                            )}
                                        </Typography>
                                    </Box>
                                )}
                                {!isTalon && (
                                    <Box display="flex" justifyContent="space-between">
                                        <Typography
                                            className="metric-row-label"
                                            sx={{ color: 'text.secondary', mt: 0.25 }}
                                        >
                                            Out Schedule
                                        </Typography>
                                        <Typography className="metric-row-value" sx={{ mt: 0.25 }}>
                                            {fmtTime(
                                                (pcaSummary as any)?.data?.data?.campaign_stats
                                                    ?.time_received_out_of_schedule,
                                            )}
                                        </Typography>
                                    </Box>
                                )}
                                {!isTalon && (
                                    <Box display="flex" justifyContent="space-between">
                                        <Typography
                                            className="metric-row-label"
                                            sx={{ color: 'text.secondary', mt: 0.25 }}
                                        >
                                            Unbooked
                                        </Typography>
                                        <Typography className="metric-row-value" sx={{ mt: 0.25 }}>
                                            {fmtTime(
                                                (pcaSummary as any)?.data?.data?.campaign_stats
                                                    ?.time_received_unbooked,
                                            )}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        </Grid>
                    </Grid>
                </Box>

                {/* First-page footer (logo + rule) */}
                <Box className="page-footer">
                    <Stack pl={2.2} direction="row" alignItems="flex-end" gap={2} sx={{ width: 1 }}>
                        <Box
                            component="img"
                            src={poweredByUniledLogo}
                            alt="Powered by UniLED"
                            sx={{
                                maxHeight: 48,
                                maxWidth: 270,
                                objectFit: 'contain',
                                display: 'block',
                            }}
                        />
                        <Box
                            sx={{ flexGrow: 1, borderBottom: '1px solid', borderColor: 'black' }}
                        />
                    </Stack>
                </Box>
            </Box>
            {/* --------- PAGE 2 --------- */}
            <TwoChartPage
                mainTitle="Total Campaign Performance"
                brand={brand}
                logoUrl={logoUrl}
                footer={{ title, campaignId: payload.campaign_id, brand: third_color }}
                showValueLabels={isTalon}
                xAxisTickFontSize={9}
                fmtCompact={fmtCompact}
                fmtInt={fmtInt}
                perfColor={perfColor}
                left={{
                    kind: 'lineRunningTotals',
                    title:
                        payload.language === 'GB'
                            ? 'Running Total Impacts'
                            : 'Running Total Impressions',
                    data: rtImpacts,
                    triplet: {
                        pct: histImpactsPct,
                        target: impactsExpected,
                        total: impactsActual,
                        label: payload.language === 'GB' ? 'Impacts' : 'Impressions',
                        targetLabel: `Target ${
                            payload.language === 'GB' ? 'Impacts' : 'Impressions'
                        }`,
                        totalLabel: `Total ${
                            payload.language === 'GB' ? 'Impacts' : 'Impressions'
                        }`,
                    },
                }}
                right={{
                    kind: 'lineRunningTotals',
                    title: 'Running Total Plays',
                    data: rtPlays,
                    triplet: {
                        pct: histPlaysPct,
                        target: playsExpected,
                        total: playsActual,
                        label: 'Plays',
                        targetLabel: 'Target Plays',
                        totalLabel: 'Total Plays',
                    },
                }}
            />
            {/* --------- PAGE 3 --------- */}
            <TwoChartPage
                mainTitle={
                    payload.language === 'GB'
                        ? 'Performance By Media Owner'
                        : 'Performance By Partner'
                }
                brand={brand}
                logoUrl={logoUrl}
                footer={{ title, campaignId: payload.campaign_id, brand: third_color }}
                showValueLabels={isTalon}
                xAxisTickFontSize={9}
                fmtCompact={fmtCompact}
                fmtInt={fmtInt}
                perfColor={perfColor}
                left={{
                    kind: 'barTotalVsExpected',
                    title:
                        payload.language === 'GB'
                            ? 'Impacts by Media Owner'
                            : 'Impressions by Partner',
                    data: owners,
                    xKey: 'name',
                    totalKey: 'impacts_total',
                    expectedKey: 'impacts_expected',
                    names: {
                        total: payload.language === 'GB' ? 'Total Impacts' : 'Total Impressions',
                        expected:
                            payload.language === 'GB' ? 'Expected Impacts' : 'Expected Impressions',
                    },
                }}
                right={{
                    kind: 'barTotalVsExpected',
                    title: payload.language === 'GB' ? 'Plays by Media Owner' : 'Plays by Partner',
                    data: owners,
                    xKey: 'name',
                    totalKey: 'plays_total',
                    expectedKey: 'plays_expected',
                    names: { total: 'Total Plays', expected: 'Expected Plays' },
                }}
            />
            <TwoChartPage
                mainTitle="Performance By Environment"
                brand={brand}
                logoUrl={logoUrl}
                footer={{ title, campaignId: payload.campaign_id, brand: third_color }}
                showValueLabels={isTalon}
                fmtCompact={fmtCompact}
                fmtInt={fmtInt}
                perfColor={perfColor}
                left={{
                    kind: 'barTotalVsExpected',
                    title:
                        payload.language === 'GB'
                            ? 'Impacts by Environment'
                            : 'Impressions by Environment',
                    data: envRows,
                    xKey: 'name',
                    totalKey: 'impacts_total',
                    expectedKey: 'impacts_expected',
                    names: {
                        total: payload.language === 'GB' ? 'Total Impacts' : 'Total Impressions',
                        expected:
                            payload.language === 'GB' ? 'Expected Impacts' : 'Expected Impressions',
                    },
                }}
                right={{
                    kind: 'barTotalVsExpected',
                    title: 'Plays by Environment',
                    data: envRows,
                    xKey: 'name',
                    totalKey: 'plays_total',
                    expectedKey: 'plays_expected',
                    names: { total: 'Total Plays', expected: 'Expected Plays' },
                }}
            />
            {isTalon && (
                <TwoChartPage
                    mainTitle="Performance By Format"
                    brand={brand}
                    logoUrl={logoUrl}
                    footer={{ title, campaignId: payload.campaign_id, brand: third_color }}
                    showValueLabels={isTalon}
                    xAxisTickFontSize={9}
                    fmtCompact={fmtCompact}
                    fmtInt={fmtInt}
                    perfColor={perfColor}
                    left={{
                        kind: 'barTotalVsExpected',
                        title:
                            payload.language === 'GB'
                                ? 'Impacts by Format'
                                : 'Impressions by Format',
                        data: formatRows,
                        xKey: 'name',
                        totalKey: 'impacts_total',
                        expectedKey: 'impacts_expected',
                        names: {
                            total:
                                payload.language === 'GB' ? 'Total Impacts' : 'Total Impressions',
                            expected:
                                payload.language === 'GB'
                                    ? 'Expected Impacts'
                                    : 'Expected Impressions',
                        },
                    }}
                    right={{
                        kind: 'barTotalVsExpected',
                        title: 'Plays by Format',
                        data: formatRows,
                        xKey: 'name',
                        totalKey: 'plays_total',
                        expectedKey: 'plays_expected',
                        names: { total: 'Total Plays', expected: 'Expected Plays' },
                    }}
                />
            )}
            {isTalon && (
                <TwoChartPage
                    mainTitle="Performance By Environment + Format"
                    brand={brand}
                    logoUrl={logoUrl}
                    footer={{ title, campaignId: payload.campaign_id, brand: third_color }}
                    showValueLabels={isTalon}
                    xAxisTickFontSize={9}
                    fmtCompact={fmtCompact}
                    fmtInt={fmtInt}
                    perfColor={perfColor}
                    left={{
                        kind: 'barTotalVsExpected',
                        title:
                            payload.language === 'GB'
                                ? 'Impacts by Environment + Format'
                                : 'Impressions by Environment + Format',
                        data: envFormatRows,
                        xKey: 'name',
                        totalKey: 'impacts_total',
                        expectedKey: 'impacts_expected',
                        names: {
                            total:
                                payload.language === 'GB' ? 'Total Impacts' : 'Total Impressions',
                            expected:
                                payload.language === 'GB'
                                    ? 'Expected Impacts'
                                    : 'Expected Impressions',
                        },
                    }}
                    right={{
                        kind: 'barTotalVsExpected',
                        title: 'Plays by Environment + Format',
                        data: envFormatRows,
                        xKey: 'name',
                        totalKey: 'plays_total',
                        expectedKey: 'plays_expected',
                        names: { total: 'Total Plays', expected: 'Expected Plays' },
                    }}
                />
            )}
            <TwoChartPage
                mainTitle={
                    payload.language === 'GB' ? 'Performance By Region' : 'Performance By State'
                }
                brand={brand}
                logoUrl={logoUrl}
                footer={{ title, campaignId: payload.campaign_id, brand: third_color }}
                showValueLabels={isTalon}
                xAxisTickFontSize={9}
                fmtCompact={fmtCompact}
                fmtInt={fmtInt}
                perfColor={perfColor}
                left={{
                    kind: 'barTotalVsExpected',
                    title: payload.language === 'GB' ? 'Impacts by Region' : 'Impressions by State',
                    data: regionRows,
                    xKey: 'name',
                    totalKey: 'impacts_total',
                    expectedKey: 'impacts_expected',
                    names: {
                        total: payload.language === 'GB' ? 'Total Impacts' : 'Total Impressions',
                        expected:
                            payload.language === 'GB' ? 'Expected Impacts' : 'Expected Impressions',
                    },
                }}
                right={{
                    kind: 'barTotalVsExpected',
                    title: payload.language === 'GB' ? 'Plays by Region' : 'Plays by State',
                    data: regionRows,
                    xKey: 'name',
                    totalKey: 'plays_total',
                    expectedKey: 'plays_expected',
                    names: { total: 'Total Plays', expected: 'Expected Plays' },
                }}
            />
            {!isTalon && (
                <TwoMapPage
                    mainTitle={payload.language === 'GB' ? 'Performance Maps' : 'Performance Maps'}
                    brand={brand}
                    logoUrl={logoUrl}
                    footer={{ title, campaignId: payload.campaign_id, brand: third_color }}
                    left={{
                        title:
                            payload.language === 'GB' ? 'Impacts Heatmap' : 'Impressions Heatmap',
                        data: mapData,
                        mode: 'heat',
                    }}
                    right={{
                        title: 'Screen Locations',
                        data: mapData,
                        mode: 'markers',
                    }}
                />
            )}
            {isTalon && (
                <OneMapPage
                    mainTitle="Performance Map"
                    brand={brand}
                    logoUrl={logoUrl}
                    footer={{ title, campaignId: payload.campaign_id, brand: third_color }}
                    map={{
                        title:
                            payload.language === 'GB' ? 'Impacts Heatmap' : 'Impressions Heatmap',
                        data: mapData,
                        mode: 'heat',
                    }}
                />
            )}
            <TwoChartPage
                mainTitle={
                    payload.language === 'GB'
                        ? 'Performance by Day of Week'
                        : 'Performance by Day of Week'
                }
                brand={brand}
                logoUrl={logoUrl}
                footer={{ title, campaignId: payload.campaign_id, brand: third_color }}
                showValueLabels={isTalon}
                xAxisTickFontSize={9}
                fmtCompact={fmtCompact}
                fmtInt={fmtInt}
                perfColor={perfColor}
                left={{
                    kind: 'barTotalVsExpected',
                    title:
                        payload.language === 'GB'
                            ? 'Impacts by Day of Week'
                            : 'Impressions by Day of Week',
                    data: dowRows,
                    xKey: 'name',
                    totalKey: 'impacts_total',
                    expectedKey: 'impacts_expected',
                    names: {
                        total: payload.language === 'GB' ? 'Total Impacts' : 'Total Impressions',
                        expected:
                            payload.language === 'GB' ? 'Expected Impacts' : 'Expected Impressions',
                    },
                }}
                right={{
                    kind: 'barTotalVsExpected',
                    title: 'Plays by Day of Week',
                    data: dowRows,
                    xKey: 'name',
                    totalKey: 'plays_total',
                    expectedKey: 'plays_expected',
                    names: { total: 'Total Plays', expected: 'Expected Plays' },
                }}
            />
            <TwoChartPage
                mainTitle="Performance by Hour Of Day"
                brand={brand}
                logoUrl={logoUrl}
                footer={{ title, campaignId: payload.campaign_id, brand: third_color }}
                showValueLabels={false}
                xAxisTickFontSize={9}
                fmtCompact={fmtCompact}
                fmtInt={fmtInt}
                perfColor={perfColor}
                left={{
                    kind: 'barTotalVsExpected',
                    title: payload.language === 'GB' ? 'Impacts by Hour' : 'Impressions by Hour',
                    data: hourRows,
                    xKey: 'name',
                    totalKey: 'impacts_total',
                    expectedKey: 'impacts_expected',
                    names: {
                        total: payload.language === 'GB' ? 'Total Impacts' : 'Total Impressions',
                        expected:
                            payload.language === 'GB' ? 'Expected Impacts' : 'Expected Impressions',
                    },
                }}
                right={{
                    kind: 'barTotalVsExpected',
                    title: 'Plays by Hour',
                    data: hourRows,
                    xKey: 'name',
                    totalKey: 'plays_total',
                    expectedKey: 'plays_expected',
                    names: { total: 'Total Plays', expected: 'Expected Plays' },
                }}
            />
            {!isTalon && (
                <Box
                    className="page"
                    sx={{ mx: 'auto', fontFamily: 'Inter, system-ui, sans-serif' }}
                >
                    <Box className="page-header">
                        <ReportHeader brand={brand} logoUrl={logoUrl} />
                    </Box>

                    <Box sx={{ pt: 4, pl: { xs: 3, md: 8 }, pr: { xs: 2, md: 6 } }}>
                        <Box
                            sx={{
                                display: 'inline-block',
                                bgcolor: brand,
                                borderRadius: 2,
                                px: 2.25,
                                py: 0.75,
                                mb: 5,
                            }}
                        >
                            <Typography
                                variant="h3"
                                sx={{ fontWeight: 900, color: '#fff', letterSpacing: 0.2 }}
                            >
                                Glossary
                            </Typography>
                        </Box>
                        <Box
                            className="glossary-grid"
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', md: 'max-content 1.25ch 1fr' }, // tight "=" column
                                columnGap: { xs: 1.5, md: 3 },
                                rowGap: { xs: 2.5, md: 3 }, // ~20px / 24px on screen
                                alignItems: 'start',
                            }}
                        >
                            {[
                                {
                                    term: 'In Schedule',
                                    def: `Plays or ${
                                        payload.language === 'GB' ? 'Impacts' : 'Impressions'
                                    } on booked units running during the booked times`,
                                },
                                {
                                    term: 'Out of Schedule',
                                    def: `Plays or ${
                                        payload.language === 'GB' ? 'Impacts' : 'Impressions'
                                    } on booked units running outside of booked times`,
                                },
                                {
                                    term: 'Unbooked',
                                    def: `Plays or ${
                                        payload.language === 'GB' ? 'Impacts' : 'Impressions'
                                    } on a unit that was not in the original booked plan`,
                                },
                            ].map(({ term, def }) => (
                                <React.Fragment key={term}>
                                    <Typography sx={{ fontWeight: 800 }}>{term}</Typography>
                                    <Typography sx={{ textAlign: 'center' }}>=</Typography>
                                    <Typography>{def}</Typography>
                                </React.Fragment>
                            ))}
                        </Box>
                    </Box>

                    <Box className="page-footer">
                        <ReportFooter
                            title={title}
                            campaignId={payload.campaign_id}
                            brand={third_color}
                        />
                    </Box>
                </Box>
            )}
            {/* --------- FINAL SLIDE --------- */}
            <Box className="page" sx={{ mx: 'auto', fontFamily: 'Inter, system-ui, sans-serif' }}>
                <Box className="page-header" sx={{ visibility: 'hidden' }}>
                    <ReportHeader brand={brand} logoUrl={logoUrl} />
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        pl: { xs: 2, md: 6 },
                        minHeight: 'calc(100% - 0px)',
                    }}
                >
                    <Box>
                        <Typography
                            variant="h1"
                            className="final-title"
                            sx={{
                                fontWeight: 900,
                                lineHeight: 0.95,
                                letterSpacing: '-0.02em',
                                fontSize: { xs: 45, md: 100 },
                            }}
                        >
                            Thank you
                        </Typography>

                        <Box
                            className="final-bar"
                            sx={{
                                mt: 2,
                                mb: 3,
                                height: 10,
                                width: { xs: 240, md: 520 },
                                bgcolor: third_color,
                                borderRadius: 1.5,
                            }}
                        />

                        <Stack direction="row" alignItems="center" spacing={3}>
                            <Box
                                component="img"
                                src={uniledLogo}
                                alt="UniLED"
                                className="final-logo"
                                sx={{ height: 36, objectFit: 'contain', display: 'block' }}
                            />
                            <Box
                                sx={{ height: 28, borderLeft: '1px solid', borderColor: 'divider' }}
                            />
                            <Box
                                component="img"
                                src={logoUrl}
                                alt="Partner"
                                className="final-logo"
                                sx={{ height: 36, objectFit: 'contain', display: 'block' }}
                            />
                        </Stack>
                    </Box>
                </Box>

                <Box className="page-footer" sx={{ visibility: 'hidden' }}>
                    <ReportFooter
                        title={title}
                        campaignId={payload.campaign_id}
                        brand={third_color}
                    />
                </Box>
            </Box>
        </>
    );
};

export default LinearReport;
